import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { getOrganisationTree, GetOrganisationTreeData } from "@/api";
import { appState } from "@/app/appState";
import { organisation_tree_node } from "@/app/typing/organisations";
import { Request } from "@/components/CRUD";
import { ScrollBox } from "@/components/UI/page";
import { ArrowDown, ArrowRight } from "@/components/UI/svgs";
import { KvpEditor, KvpList, useKvp } from "@/app/workspace/kvp/kvpPage";
import { I18nEditor, I18nTree, useI18nAdmin } from "@/app/workspace/i18n/i18nEditor";
import { ancestorsOf } from "./tree";
import { ancestorsOfPath } from "./keyTree";
import { OrgTree } from "./orgTree";
import { isOrgTab, NewOrgInspector, OrgInspector, OrgTab } from "./orgInspector";
import { PromptSetInspector } from "./promptSetInspector";
import { scrollSettingsRow } from "./ui";
import { isModelsSection, ModelsInspector, ModelsNav, ModelsSection } from "./models/modelsInspector";

const ROOT = "/workspace/settings";

type Selection =
    | { kind: "i18n"; key?: string }
    | { kind: "kvp"; key?: string }
    | { kind: "models"; section: ModelsSection }
    | { kind: "org"; orgId: number; tab: OrgTab }
    | { kind: "new-org"; parentId: number }
    | { kind: "set"; orgId: number; setId: number };

type NavTarget =
    | Exclude<Selection, { kind: "org" }>
    | { kind: "org"; orgId: number; tab?: OrgTab }
    | { kind: "home" };

/**
 * Selection lives in the URL so a sysadmin can hand someone a link to the exact
 * organisation or prompt set they are talking about. i18n/kvp trees also include
 * expected-but-unregistered keys from i18nKeyRegistry / kvpKeyRegistry.
 */
const parseSelection = (pathname: string): Selection | undefined => {
    const rest = pathname.replace(/^\/workspace\/settings\/?/, "");

    if (rest === "i18n" || rest.startsWith("i18n/")) {
        const key = rest === "i18n" ? undefined : decodeURIComponent(rest.slice("i18n/".length));
        return { kind: "i18n", key: key || undefined };
    }

    if (rest === "kvp" || rest.startsWith("kvp/")) {
        const key = rest === "kvp" ? undefined : decodeURIComponent(rest.slice("kvp/".length));
        return { kind: "kvp", key: key || undefined };
    }

    if (rest === "models" || rest.startsWith("models/")) {
        const section = rest.slice("models/".length);
        return { kind: "models", section: isModelsSection(section) ? section : "assignments" };
    }

    const newOrg = rest.match(/^org\/new\/(\d+)$/);
    if (newOrg) return { kind: "new-org", parentId: parseInt(newOrg[1]) };

    const org = rest.match(/^org\/(\d+)(?:\/([a-z]+))?$/);
    if (org) {
        return { kind: "org", orgId: parseInt(org[1]), tab: org[2] && isOrgTab(org[2]) ? org[2] : "billing" };
    }

    const set = rest.match(/^set\/(\d+)\/(new|\d+)$/);
    if (set) {
        return {
            kind: "set",
            orgId: parseInt(set[1]),
            setId: set[2] === "new" ? -1 : parseInt(set[2]),
        };
    }

    return undefined;
};

/**
 * The whole navigation column is one scroll area, so a fold only contributes its
 * natural height. The header sticks to the top of that column while its own fold is
 * in view, which needs an opaque background for the rows passing underneath.
 */
const SidebarFold = observer(({
    title,
    open,
    onToggle,
    children,
}: {
    title: string;
    open: boolean;
    onToggle: () => void;
    children: ReactNode;
}) => (
    <div className="fold">
        <div onClick={onToggle} className="fold-header">
            <span className="fold-chevron">
                {open ? <ArrowDown size={14} /> : <ArrowRight size={14} />}
            </span>
            <p className="fold-title">{title}</p>
        </div>
        {open && (
            <div className="fold-body">
                {children}
            </div>
        )}
    </div>
));

export const SettingsPage = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const kvp = useKvp();
    const i18n = useI18nAdmin();

    const [nodes, setNodes] = useState<organisation_tree_node[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [i18nSearch, setI18nSearch] = useState("");
    const [i18nExpanded, setI18nExpanded] = useState<Set<string>>(new Set());
    const [kvpSearch, setKvpSearch] = useState("");
    const [kvpExpanded, setKvpExpanded] = useState<Set<string>>(new Set());
    const [i18nOpen, setI18nOpen] = useState(false);
    const [kvpOpen, setKvpOpen] = useState(false);
    const [modelsOpen, setModelsOpen] = useState(false);
    const [orgsOpen, setOrgsOpen] = useState(false);

    const selection = parseSelection(location.pathname);

    const loadTree = Request<GetOrganisationTreeData, any>(
        getOrganisationTree,
        () => ({}),
        setLoading,
        (data: any) => setNodes(data.organisations ?? []),
        () => setNodes([]),
        () => true,
    );

    useEffect(() => {
        loadTree();
    }, [appState.user?.active_organisation?.id]);

    const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);

    const defaultUsage = useMemo(() => {
        const usage = new Map<number, number>();
        for (const node of nodes) {
            if (node.default_prompt_set_id) {
                usage.set(node.default_prompt_set_id, (usage.get(node.default_prompt_set_id) ?? 0) + 1);
            }
        }
        return usage;
    }, [nodes]);

    const toggleExpanded = useCallback((id: number) => {
        setExpanded((previous) => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const selectedOrgId =
        selection?.kind === "new-org" ? selection.parentId
            : selection?.kind === "org" || selection?.kind === "set" ? selection.orgId
                : undefined;
    const selectedNode = selectedOrgId !== undefined ? byId.get(selectedOrgId) : undefined;

    const toggleI18nExpanded = useCallback((path: string) => {
        setI18nExpanded((previous) => {
            const next = new Set(previous);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }, []);

    const toggleKvpExpanded = useCallback((path: string) => {
        setKvpExpanded((previous) => {
            const next = new Set(previous);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }, []);

    const goTo = useCallback((next: NavTarget) => {
        if (next.kind !== "i18n") i18n.clearSelection();
        if (next.kind !== "kvp") kvp.clearSelection();

        if (next.kind === "home") {
            navigate(ROOT);
            return;
        }

        if (next.kind === "i18n") {
            setI18nOpen(true);
            if (next.key) {
                const key = next.key;
                void (async () => {
                    await i18n.ensureKey(key);
                    setI18nExpanded((previous) => new Set([...previous, ...ancestorsOfPath(key)]));
                    navigate(`${ROOT}/i18n/${encodeURIComponent(key)}`);
                    scrollSettingsRow("i18n", key);
                })();
            } else {
                i18n.clearSelection();
                navigate(`${ROOT}/i18n`);
            }
            return;
        }

        if (next.kind === "kvp") {
            setKvpOpen(true);
            if (next.key) {
                const key = next.key;
                // A branch (system_prompt, tool, ...) is not a row: show its overview instead.
                if (kvp.rows.some((row) => row.key === key)) kvp.selectKey(key);
                else kvp.clearSelection();
                setKvpExpanded((previous) => new Set([...previous, ...ancestorsOfPath(key)]));
                navigate(`${ROOT}/kvp/${encodeURIComponent(key)}`);
                scrollSettingsRow("kvp", key);
            } else {
                kvp.clearSelection();
                navigate(`${ROOT}/kvp`);
            }
            return;
        }

        if (next.kind === "models") {
            setModelsOpen(true);
            navigate(`${ROOT}/models/${next.section}`);
            return;
        }

        setOrgsOpen(true);
        const orgId = next.kind === "new-org" ? next.parentId : next.orgId;
        setExpanded((previous) => new Set([...previous, ...ancestorsOf(nodes, orgId)]));
        if (next.kind === "org") navigate(`${ROOT}/org/${next.orgId}${next.tab ? `/${next.tab}` : ""}`);
        else if (next.kind === "new-org") navigate(`${ROOT}/org/new/${next.parentId}`);
        else navigate(`${ROOT}/set/${next.orgId}/${next.setId < 0 ? "new" : next.setId}`);
        scrollSettingsRow("org", orgId);
    }, [navigate, nodes, i18n.clearSelection, i18n.ensureKey, kvp.clearSelection, kvp.selectKey]);

    const openI18n = useCallback((key?: string) => goTo({ kind: "i18n", key }), [goTo]);
    const openKvp = useCallback((key?: string) => goTo({ kind: "kvp", key }), [goTo]);
    const selectOrg = useCallback((orgId: number, tab?: OrgTab) => goTo({ kind: "org", orgId, tab }), [goTo]);
    // Moving between organisations keeps the open tab, so the same page can be compared.
    const currentTab = selection?.kind === "org" ? selection.tab : undefined;

    useEffect(() => {
        const activeOrgId = appState.user?.active_organisation?.id;
        if (!selection && activeOrgId && byId.has(activeOrgId)) selectOrg(activeOrgId);
    }, [selection, byId, selectOrg]);

    useEffect(() => {
        if (selection?.kind === "i18n") setI18nOpen(true);
        if (selection?.kind === "kvp") setKvpOpen(true);
        if (selection?.kind === "models") setModelsOpen(true);
        if (selection?.kind === "org" || selection?.kind === "set" || selection?.kind === "new-org") {
            setOrgsOpen(true);
        }
    }, [selection?.kind]);

    useEffect(() => {
        if (selection?.kind !== "i18n") {
            if (i18n.selectedKey != null) i18n.clearSelection();
            return;
        }
        if (!selection.key || i18n.selectedKey === selection.key) return;
        if (!i18n.entries.some((entry) => entry.key === selection.key)) return;
        i18n.selectKey(selection.key);
        setI18nExpanded((previous) => new Set([...previous, ...ancestorsOfPath(selection.key!)]));
    }, [selection, i18n.entries.length, i18n.loading, i18n.selectedKey, i18n.clearSelection, i18n.selectKey]);

    useEffect(() => {
        if (selection?.kind !== "kvp") {
            if (kvp.selected != null) kvp.clearSelection();
            return;
        }
        if (!selection.key || kvp.selected?.key === selection.key) return;
        if (!kvp.rows.some((row) => row.key === selection.key)) return;
        kvp.selectKey(selection.key);
        setKvpExpanded((previous) => new Set([...previous, ...ancestorsOfPath(selection.key!)]));
    }, [selection, kvp.rows.length, kvp.loading, kvp.selected, kvp.clearSelection, kvp.selectKey]);

    // While a prompt set is open, mark the organisations it is the default for.
    const markedOrgIds = useMemo(() => {
        if (selection?.kind !== "set" || selection.setId < 0) return undefined;
        return new Set(
            nodes.filter((node) => node.default_prompt_set_id === selection.setId).map((node) => node.id),
        );
    }, [selection, nodes]);

    // A kvp URL that names a branch of the tree rather than an entry.
    const kvpGroupPath =
        selection?.kind === "kvp" && selection.key && !kvp.rows.some((row) => row.key === selection.key)
            ? selection.key
            : null;

    const inspector = () => {
        if (selection?.kind === "i18n") {
            return i18n.isSuperAdmin
                ? <I18nEditor
                    i18n={i18n}
                    kvpRows={kvp.rows}
                    onOpenKvp={openKvp}
                    onDeleted={() => goTo({ kind: "i18n" })}
                />
                : <p className="inspector-empty-text">{appState.t("settings.unauthorized")}</p>;
        }

        if (selection?.kind === "kvp") {
            return kvp.isSuperAdmin
                ? <KvpEditor
                    kvp={kvp}
                    groupPath={kvpGroupPath}
                    onOpenKey={openKvp}
                    onOpenI18n={openI18n}
                    onEnsureI18n={i18n.ensureKey}
                    onSaved={async () => {
                        await i18n.load();
                        await appState.loadStrings(appState.locale);
                    }}
                    onDeleted={() => goTo({ kind: "kvp" })}
                />
                : <p className="inspector-empty-text">{appState.t("settings.unauthorized")}</p>;
        }

        if (selection?.kind === "models") {
            return kvp.isSuperAdmin
                ? <ModelsInspector section={selection.section} />
                : <p className="inspector-empty-text">{appState.t("settings.unauthorized")}</p>;
        }

        if (!selection || !selectedNode) {
            return (
                <div className="inspector-empty">
                    <p className="inspector-empty-title">{appState.t("settings.pickOrganisation")}</p>
                    <p className="inspector-empty-text max-w-[28rem]">
                        {appState.t("settings.organisationTreeHint")}
                    </p>
                </div>
            );
        }

        if (selection.kind === "new-org") {
            return (
                <NewOrgInspector
                    parent={selectedNode}
                    onCreated={(orgId) => {
                        loadTree();
                        if (orgId) selectOrg(orgId);
                    }}
                    onCancel={() => selectOrg(selectedNode.id)}
                />
            );
        }

        if (selection.kind === "set") {
            return (
                <PromptSetInspector
                    org={selectedNode}
                    setId={selection.setId}
                    usedBy={nodes.filter((node) => node.default_prompt_set_id === selection.setId)}
                    onBack={() => selectOrg(selectedNode.id, "prompts")}
                    onSelectOrg={selectOrg}
                    onChanged={(newSetId) => {
                        loadTree();
                        if (selectedNode.id === appState.user?.active_organisation?.id) {
                            appState.getAgents({ pageNumber: 0, search: "" });
                        }
                        if (newSetId && newSetId !== selection.setId) {
                            goTo({ kind: "set", orgId: selectedNode.id, setId: newSetId });
                        }
                    }}
                />
            );
        }

        return (
            <OrgInspector
                key={selectedNode.id}
                node={selectedNode}
                holder={
                    selectedNode.billing_organisation_id
                        ? byId.get(selectedNode.billing_organisation_id)
                        : undefined
                }
                tab={selection.tab}
                defaultUsage={defaultUsage}
                onTab={(tab) => selectOrg(selectedNode.id, tab)}
                onSelectOrg={(orgId) => selectOrg(orgId)}
                onOpenPromptSet={(setId) => goTo({ kind: "set", orgId: selectedNode.id, setId })}
                onNewPromptSet={() => goTo({ kind: "set", orgId: selectedNode.id, setId: -1 })}
                onRefresh={loadTree}
                onDeactivated={() => {
                    loadTree();
                    goTo({ kind: "home" });
                }}
            />
        );
    };

    return (
        // Deliberately not PageBox: that would make one scroller out of both panes, so
        // scrolling a tree would drag the inspector along. pt-3 clears the fixed navbar.
        <div className="split-layout">
        <div className="split-frame">
            <div className="split-sidebar">
                {kvp.isSuperAdmin && (
                    <SidebarFold title={appState.t("settings.i18n")} open={i18nOpen} onToggle={() => setI18nOpen((open) => !open)}>
                        <I18nTree
                            i18n={i18n}
                            search={i18nSearch}
                            setSearch={setI18nSearch}
                            expanded={i18nExpanded}
                            toggleExpanded={toggleI18nExpanded}
                            onSelect={(key) => openI18n(key)}
                            onAdd={() => {
                                i18n.addRow();
                                setI18nOpen(true);
                                if (selection?.kind !== "i18n") navigate(`${ROOT}/i18n`);
                            }}
                            onOpenKvp={openKvp}
                        />
                    </SidebarFold>
                )}
                {kvp.isSuperAdmin && (
                    <SidebarFold title={appState.t("settings.kvp")} open={kvpOpen} onToggle={() => setKvpOpen((open) => !open)}>
                        <KvpList
                            kvp={kvp}
                            search={kvpSearch}
                            setSearch={setKvpSearch}
                            expanded={kvpExpanded}
                            toggleExpanded={toggleKvpExpanded}
                            onSelect={(key) => openKvp(key)}
                            onSelectGroup={(path) => openKvp(path)}
                            activePath={selection?.kind === "kvp" ? selection.key ?? null : null}
                            onAdd={() => {
                                kvp.addRow();
                                setKvpOpen(true);
                                if (selection?.kind !== "kvp") navigate(`${ROOT}/kvp`);
                            }}
                            onOpenI18n={openI18n}
                        />
                    </SidebarFold>
                )}
                {kvp.isSuperAdmin && (
                    <SidebarFold title={appState.t("settings.models")} open={modelsOpen} onToggle={() => setModelsOpen((open) => !open)}>
                        <ModelsNav
                            selected={selection?.kind === "models" ? selection.section : undefined}
                            onSelect={(section) => goTo({ kind: "models", section })}
                        />
                    </SidebarFold>
                )}
                <SidebarFold title={appState.t("settings.organisations")} open={orgsOpen} onToggle={() => setOrgsOpen((open) => !open)}>
                    <OrgTree
                        nodes={nodes}
                        loading={loading}
                        search={search}
                        setSearch={setSearch}
                        expanded={expanded}
                        toggleExpanded={toggleExpanded}
                        selectedOrgId={selectedOrgId}
                        markedOrgIds={markedOrgIds}
                        onSelect={(orgId) => {
                            setOrgsOpen(true);
                            selectOrg(orgId, currentTab);
                        }}
                        onCreateChild={(parentId) => goTo({ kind: "new-org", parentId })}
                    />
                </SidebarFold>
            </div>

            <div className="split-main">
                <ScrollBox>
                    <div className="w-full max-w-[52rem] px-2 pb-4">{inspector()}</div>
                </ScrollBox>
            </div>
        </div>
        </div>
    );
});

export default SettingsPage;
